import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Lightbulb } from "lucide-react";
import type { CategoryDimension, CategoryOption } from "@shared/schema";

interface SuggestCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dimensionId?: string;
  submittedByCompanyId?: string;
}

export function SuggestCategoryModal({ open, onOpenChange, dimensionId: fixedDimensionId, submittedByCompanyId }: SuggestCategoryModalProps) {
  const { toast } = useToast();
  const [selectedDimensionId, setSelectedDimensionId] = useState<string>(fixedDimensionId ?? "");
  const [sectionId, setSectionId] = useState<string>("");
  const [groupId, setGroupId] = useState<string>("");
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [articleLabel, setArticleLabel] = useState("");
  const [acronym, setAcronym] = useState("");
  const [rootSlug, setRootSlug] = useState("");

  const activeDimensionId = fixedDimensionId ?? selectedDimensionId;

  const { data: dimensions = [] } = useQuery<CategoryDimension[]>({
    queryKey: ["/api/categories/dimensions"],
    enabled: !fixedDimensionId,
  });

  const { data: allOptions = [] } = useQuery<CategoryOption[]>({
    queryKey: ["/api/categories/options"],
  });

  const dimensionOptions = useMemo(
    () => allOptions.filter(o => o.dimensionId === activeDimensionId),
    [allOptions, activeDimensionId]
  );

  const sections = useMemo(() => dimensionOptions.filter(o => o.depth === 0), [dimensionOptions]);
  const groupsForSection = useMemo(
    () => dimensionOptions.filter(o => o.depth === 1 && o.parentId === sectionId),
    [dimensionOptions, sectionId]
  );

  const showArticleLabel = useMemo(() => dimensionOptions.some(o => o.articleLabel), [dimensionOptions]);
  const showAcronym = useMemo(() => dimensionOptions.some(o => o.acronym), [dimensionOptions]);
  const showRootSlug = useMemo(() => dimensionOptions.some(o => o.rootSlug), [dimensionOptions]);

  const proposedParentId = groupId || sectionId || null;

  const submitMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/categories/suggestions", {
        dimensionId: activeDimensionId,
        proposedDisplayName: displayName.trim(),
        proposedDescription: description.trim() || null,
        proposedParentId,
        proposedArticleLabel: articleLabel.trim() || null,
        proposedAcronym: acronym.trim() || null,
        proposedRootSlug: rootSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "") || null,
        submittedByCompanyId: submittedByCompanyId || null,
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Suggestion submitted", description: "An admin will review your suggestion shortly." });
      handleClose();
    },
    onError: () => {
      toast({ title: "Failed to submit suggestion", variant: "destructive" });
    },
  });

  const handleClose = () => {
    setDisplayName("");
    setDescription("");
    setArticleLabel("");
    setAcronym("");
    setRootSlug("");
    setSectionId("");
    setGroupId("");
    if (!fixedDimensionId) setSelectedDimensionId("");
    onOpenChange(false);
  };

  const canSubmit = activeDimensionId && displayName.trim().length > 0;

  const activeDimension = fixedDimensionId
    ? null
    : dimensions.find(d => d.id === activeDimensionId);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            Suggest a new category
          </DialogTitle>
          <DialogDescription>
            Your suggestion will be reviewed by an admin before it appears in the category list.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {!fixedDimensionId && (
            <div className="flex flex-col gap-1.5">
              <Label>Dimension</Label>
              <Select value={selectedDimensionId} onValueChange={(v) => { setSelectedDimensionId(v); setSectionId(""); setGroupId(""); }}>
                <SelectTrigger data-testid="select-suggest-dimension">
                  <SelectValue placeholder="Select a dimension..." />
                </SelectTrigger>
                <SelectContent>
                  {dimensions.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.displayName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {activeDimensionId && sections.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <Label>Section <span className="text-muted-foreground text-xs">(optional — where does this belong?)</span></Label>
              <Select value={sectionId} onValueChange={(v) => { setSectionId(v === "__none__" ? "" : v); setGroupId(""); }}>
                <SelectTrigger data-testid="select-suggest-section">
                  <SelectValue placeholder="Top-level (new section)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Top-level (new section)</SelectItem>
                  {sections.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.displayName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {sectionId && groupsForSection.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <Label>Group <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Select value={groupId} onValueChange={(v) => setGroupId(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="select-suggest-group">
                  <SelectValue placeholder="New group under this section" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">New group (no specific group)</SelectItem>
                  {groupsForSection.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.displayName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label>Display name <span className="text-destructive">*</span></Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Machine Vision"
              data-testid="input-suggest-display-name"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Description <span className="text-muted-foreground text-xs">(optional)</span></Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this category..."
              rows={2}
              data-testid="input-suggest-description"
            />
          </div>

          {showArticleLabel && (
            <div className="flex flex-col gap-1.5">
              <Label>Article label <span className="text-muted-foreground text-xs">(optional — shown on published articles)</span></Label>
              <Input
                value={articleLabel}
                onChange={(e) => setArticleLabel(e.target.value)}
                placeholder="e.g. Machine Vision"
                data-testid="input-suggest-article-label"
              />
            </div>
          )}

          {showAcronym && (
            <div className="flex flex-col gap-1.5">
              <Label>Acronym <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                value={acronym}
                onChange={(e) => setAcronym(e.target.value.toUpperCase())}
                placeholder="e.g. MV"
                className="uppercase"
                data-testid="input-suggest-acronym"
              />
            </div>
          )}

          {showRootSlug && (
            <div className="flex flex-col gap-1.5">
              <Label>Root slug <span className="text-muted-foreground text-xs">(optional — broader concept grouping)</span></Label>
              <Input
                value={rootSlug}
                onChange={(e) => setRootSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="e.g. vision-systems"
                data-testid="input-suggest-root-slug"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => submitMutation.mutate()}
            disabled={!canSubmit || submitMutation.isPending}
            data-testid="button-submit-suggestion"
          >
            {submitMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
            ) : (
              "Submit suggestion"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
